import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, X, UserMinus, Trash2, Users, Edit
} from 'lucide-react';
import { motion } from 'framer-motion';

const ROLES = [
  { value: 'head', label: 'Head', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'assistant', label: 'Assistant', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'cit', label: 'CIT', color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'helper', label: 'Helper', color: 'bg-orange-100 text-orange-800 border-orange-300' },
];

const GROUP_ORDER = [
  'Group A - Nursery',
  'Group B - PreK',
  'Group C - Kindergarten',
  'Group D - 1st Grade',
  'Group E - 2nd Grade',
  'Group F - 3rd-5th Grade',
];

function getRoleBadge(role) {
  const roleInfo = ROLES.find(r => r.value === role) || ROLES[1];
  return <Badge className={`${roleInfo.color} border`}>{roleInfo.label}</Badge>;
}

// Sort so that roles appear: head -> assistant -> cit -> helper
const ROLE_ORDER = { head: 0, assistant: 1, cit: 2, helper: 3 };

export default function CounselorPanel({ date }) {
  const { 
    masterRoster, addCounselor, removeCounselor,
    markCounselorAbsent, addDayCounselor, removeDayCounselor,
    getCounselorsForDate
  } = useApp();

  const [addDayDialog, setAddDayDialog] = useState(false);
  const [editRosterDialog, setEditRosterDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [newCounselor, setNewCounselor] = useState({ name: '', role: 'assistant', group: '' });

  const { active, absent, temporary } = getCounselorsForDate(date);

  // Group active + temporary staff by their group
  const allStaff = [...active, ...temporary];
  const groupedStaff = {};
  const ungroupedStaff = [];

  allStaff.forEach(c => {
    const grp = c.group || '';
    if (grp) {
      if (!groupedStaff[grp]) groupedStaff[grp] = [];
      groupedStaff[grp].push(c);
    } else {
      ungroupedStaff.push(c);
    }
  });

  // Sort each group's staff by role
  Object.values(groupedStaff).forEach(list => {
    list.sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9));
  });

  // Order groups by GROUP_ORDER
  const sortedGroupNames = GROUP_ORDER.filter(g => groupedStaff[g]);
  // Add any groups not in GROUP_ORDER
  Object.keys(groupedStaff).forEach(g => {
    if (!sortedGroupNames.includes(g)) sortedGroupNames.push(g);
  });

  const handleAddToRoster = () => {
    if (newCounselor.name.trim()) {
      addCounselor({ name: newCounselor.name.trim(), role: newCounselor.role, group: newCounselor.group });
      setNewCounselor({ name: '', role: 'assistant', group: '' });
    }
  };

  const handleAddForDay = () => {
    if (newCounselor.name.trim()) {
      addDayCounselor(date, { name: newCounselor.name.trim(), role: newCounselor.role, group: newCounselor.group });
      setNewCounselor({ name: '', role: 'assistant', group: '' });
      setAddDayDialog(false);
    }
  };

  const handleConfirmDelete = () => {
    if (deleteConfirm) {
      removeCounselor(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  const hasStaff = allStaff.length > 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-[#0F172A] font-['Fredoka'] flex items-center gap-2">
          <Users className="w-5 h-5 text-[#0055A4]" />
          Staff by Group
        </h3>
        <Button size="sm" variant="outline" onClick={() => setEditRosterDialog(true)} className="rounded-lg" data-testid="edit-roster-button">
          <Edit className="w-4 h-4 mr-1" />
          Edit Roster
        </Button>
      </div>

      {/* Staff grouped by camp group */}
      {!hasStaff ? (
        <motion.div className="bg-white rounded-2xl shadow-lg border border-[#E2E8F0] p-8 text-center" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-[#475569]">No staff loaded yet. Hit Refresh on the Attendance tab to load from the Google Sheet.</p>
        </motion.div>
      ) : (
        sortedGroupNames.map((groupName, gIdx) => (
          <motion.div
            key={groupName}
            className="bg-white rounded-2xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-[#E2E8F0] overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: gIdx * 0.05 }}
            data-testid={`staff-group-${groupName.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {/* Group header */}
            <div className="flex items-center gap-3 px-5 py-3 bg-[#0055A4]/5 border-b border-[#E2E8F0]">
              <div className="w-1 h-8 bg-[#0055A4] rounded-full" />
              <h4 className="font-bold text-[#0F172A] font-['Fredoka'] text-base">{groupName}</h4>
              <Badge className="bg-blue-100 text-blue-800 ml-auto">{groupedStaff[groupName].length} staff</Badge>
            </div>
            {/* Staff rows */}
            <div className="divide-y divide-[#F1F5F9]">
              {groupedStaff[groupName].map(counselor => (
                <div key={counselor.id} className="flex items-center justify-between px-5 py-3" data-testid={`counselor-row-${counselor.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-[#0F172A]">{counselor.name}</span>
                    {getRoleBadge(counselor.role)}
                    {counselor.isTemporary && <Badge variant="outline" className="text-xs">Today only</Badge>}
                  </div>
                  <div className="flex gap-1">
                    {!counselor.isTemporary && (
                      <Button size="sm" variant="ghost" onClick={() => markCounselorAbsent(counselor.id, date, true)} className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`mark-absent-${counselor.id}`}>
                        <UserMinus className="w-4 h-4" />
                      </Button>
                    )}
                    {counselor.isTemporary && (
                      <Button size="sm" variant="ghost" onClick={() => removeDayCounselor(date, counselor.id)} className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`remove-temp-${counselor.id}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))
      )}

      {/* Ungrouped staff (manually added without a group) */}
      {ungroupedStaff.length > 0 && (
        <motion.div className="bg-white rounded-2xl shadow-lg border border-[#E2E8F0] overflow-hidden" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-[#E2E8F0]">
            <div className="w-1 h-8 bg-gray-400 rounded-full" />
            <h4 className="font-bold text-[#475569] font-['Fredoka'] text-base">Unassigned</h4>
          </div>
          <div className="divide-y divide-[#F1F5F9]">
            {ungroupedStaff.map(counselor => (
              <div key={counselor.id} className="flex items-center justify-between px-5 py-3" data-testid={`counselor-row-${counselor.id}`}>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-[#0F172A]">{counselor.name}</span>
                  {getRoleBadge(counselor.role)}
                  {counselor.isTemporary && <Badge variant="outline" className="text-xs">Today only</Badge>}
                </div>
                <div className="flex gap-1">
                  {!counselor.isTemporary && (
                    <Button size="sm" variant="ghost" onClick={() => markCounselorAbsent(counselor.id, date, true)} className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50">
                      <UserMinus className="w-4 h-4" />
                    </Button>
                  )}
                  {counselor.isTemporary && (
                    <Button size="sm" variant="ghost" onClick={() => removeDayCounselor(date, counselor.id)} className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Absent Staff */}
      {absent.length > 0 && (
        <motion.div className="bg-gray-50 rounded-2xl border border-[#E2E8F0] p-5" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <h3 className="text-sm font-bold text-[#475569] uppercase tracking-wider mb-3">Absent Today</h3>
          <div className="space-y-2">
            {absent.map(counselor => (
              <div key={counselor.id} className="flex items-center justify-between p-3 bg-white rounded-xl opacity-60">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-[#64748B] line-through">{counselor.name}</span>
                  {getRoleBadge(counselor.role)}
                  {counselor.group && <span className="text-xs text-[#94A3B8]">{counselor.group}</span>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => markCounselorAbsent(counselor.id, date, false)} className="h-8 px-3 text-green-600 hover:text-green-700 hover:bg-green-50" data-testid={`restore-${counselor.id}`}>
                  Restore
                </Button>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Add for today */}
      <Button onClick={() => setAddDayDialog(true)} variant="outline" className="w-full rounded-xl border-dashed border-2" data-testid="add-day-counselor-button">
        <Plus className="w-4 h-4 mr-2" />
        Add Counselor for Today Only
      </Button>

      {/* Add Counselor for Today Dialog */}
      <Dialog open={addDayDialog} onOpenChange={setAddDayDialog}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Fredoka']">Add Counselor for Today</DialogTitle>
            <DialogDescription>Add a temporary staff member for today only.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-[#475569]">Name</label>
              <Input value={newCounselor.name} onChange={(e) => setNewCounselor({ ...newCounselor, name: e.target.value })} placeholder="Enter counselor name" className="mt-1 h-12 rounded-xl" data-testid="new-counselor-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#475569]">Role</label>
              <Select value={newCounselor.role} onValueChange={(v) => setNewCounselor({ ...newCounselor, role: v })}>
                <SelectTrigger className="mt-1 h-12 rounded-xl" data-testid="new-counselor-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#475569]">Group</label>
              <Select value={newCounselor.group} onValueChange={(v) => setNewCounselor({ ...newCounselor, group: v })}>
                <SelectTrigger className="mt-1 h-12 rounded-xl" data-testid="new-counselor-group"><SelectValue placeholder="Select a group" /></SelectTrigger>
                <SelectContent>
                  {GROUP_ORDER.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setAddDayDialog(false)} className="flex-1 rounded-xl">Cancel</Button>
            <Button onClick={handleAddForDay} disabled={!newCounselor.name.trim()} className="flex-1 rounded-xl bg-[#0055A4] hover:bg-[#004080]" data-testid="save-day-counselor-button">Add for Today</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Master Roster Dialog */}
      <Dialog open={editRosterDialog} onOpenChange={setEditRosterDialog}>
        <DialogContent className="sm:max-w-lg rounded-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Fredoka']">Edit Master Roster</DialogTitle>
            <DialogDescription>Manage the permanent staff roster. Changes here affect all days.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {masterRoster.length === 0 ? (
              <p className="text-center text-[#94A3B8] py-4">No counselors in roster yet</p>
            ) : (
              <div className="space-y-2 mb-4">
                {masterRoster.map(counselor => (
                  <div key={counselor.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[#0F172A]">{counselor.name}</span>
                      {getRoleBadge(counselor.role)}
                      {counselor.group && <span className="text-xs text-[#94A3B8]">{counselor.group}</span>}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(counselor.id)} className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-roster-${counselor.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-bold text-[#475569] mb-3">Add New Counselor</h4>
              <div className="space-y-3">
                <Input value={newCounselor.name} onChange={(e) => setNewCounselor({ ...newCounselor, name: e.target.value })} placeholder="Enter counselor name" className="h-12 rounded-xl" />
                <div className="flex gap-3">
                  <Select value={newCounselor.role} onValueChange={(v) => setNewCounselor({ ...newCounselor, role: v })}>
                    <SelectTrigger className="h-12 rounded-xl flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={newCounselor.group} onValueChange={(v) => setNewCounselor({ ...newCounselor, group: v })}>
                    <SelectTrigger className="h-12 rounded-xl flex-1"><SelectValue placeholder="Group" /></SelectTrigger>
                    <SelectContent>{GROUP_ORDER.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button onClick={handleAddToRoster} disabled={!newCounselor.name.trim()} className="w-full rounded-xl bg-[#0055A4] hover:bg-[#004080]" data-testid="add-to-roster-button">
                  <Plus className="w-4 h-4 mr-1" />Add to Roster
                </Button>
              </div>
            </div>
          </div>
          <Button onClick={() => setEditRosterDialog(false)} className="w-full rounded-xl" data-testid="close-roster-dialog">Done</Button>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-['Fredoka']">Remove from Roster?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove this counselor from the master roster across all days.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="rounded-xl bg-red-600 hover:bg-red-700" data-testid="confirm-delete-counselor">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
